import { useCallback, useContext, useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Col,
    Container,
    Form,
    Row,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import api from "../../../service/api";
import BatchClassDetails from "./components/BatchClassDetails";
import DynamicTable from "../../../components/version-two-components/DynamicTable";
import { columns } from "./components/columns";
import TrainingDates from "./components/TrainingDates";
import Select from "react-select";
import { addDefaultOption } from "../../../utils/utility";
import { STATUS_BADGE } from "../BcaAttendance/components/StudentSession";
import { SnackbarAlert } from "../../../components/SnackbarAlert";

const ClassAbsentees = () => {
    const { id } = useParams();
    const [batchDetails, setBatchDetails] = useState({
        batch_students: [],
    });
    const [defaultAccordion, setDefaultAccordion] = useState();
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);

    const [forceSelectionIdxs, setForceSelectionIdxs] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });
    const [attendance, setAttendance] = useState("");

    const fetchBatchDetails = useCallback(
        async (session = null, date = null, accordionMenu = null) => {
            let url = `tms/batches/bca-attendance/list/${id}`;

            const params = new URLSearchParams();

            if (date) params.append("date", date);
            if (session) params.append("session", session);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await api.get(url);

            const batch = response.data.batch;

            if (!date && batch.training_dates?.length) {
                // first AM session
                let index = batch.training_dates.findIndex((d) => d.am?.from);
                let session = "AM";

                // If no AM exists, use PM
                if (index === -1) {
                    index = batch.training_dates.findIndex((d) => d.pm?.from);
                    session = "PM";
                }

                // If no PM exists, use EV
                if (index === -1) {
                    index = batch.training_dates.findIndex((d) => d.ev?.from);
                    session = "EV";
                }

                if (index !== -1) {
                    const td = batch.training_dates[index];

                    return fetchBatchDetails(
                        session,
                        td.date,
                        `trainingAccordian-${index}`
                    );
                }
            }

            setBatchDetails(batch);
            setDefaultAccordion(accordionMenu);

            setSelectedDate(date);
            setSelectedSession(session);
        },
        [id]
    );

    const resetSelectedIdxs = (indexs) => {
        var array = [...selectedRows];
        return array;
    };

    useEffect(() => {
        fetchBatchDetails();
    }, [fetchBatchDetails]);

    const batchStudents = batchDetails.batch_students;

    useEffect(() => {
        const selectedTrainingDates = batchStudents
            .filter((_, idx) => forceSelectionIdxs[idx])
            .flatMap((student) => student.student_training_dates);

        setSelectedRows(selectedTrainingDates);
    }, [batchStudents, forceSelectionIdxs]);

    const createAttendance = async () => {
        setError(null);
        const students = selectedRows.map((std) => std.id);

        if (students.length < 1) {
            setError("Please select at least one student.");
        }

        const payload = {
            students,
            session: selectedSession,
            date: selectedDate,
            status: attendance,
        };

        try {
            setLoading(true);

            
            const response = await api.post(
                "tms/batches/bca-attendance/checkbox",
                payload
            );

            if (response.success === true) {
                await fetchBatchDetails(
                    selectedSession,
                    selectedDate,
                    defaultAccordion
                );

                setNotif({
                    notifMsg: "Attendance success!",
                    open: true,
                    severity: "success",
                });
            }
        } catch (error) {
            const status = error.response?.status;
            if (status === 422) {
                setNotif({
                    notifMsg: error.response.data.message,
                    open: true,
                    severity: "warning",
                });
            } else {
                setNotif({
                    notifMsg: "Something went wrong. Please try again.",
                    open: true,
                    severity: "danger",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet title="Class Information" />
            <Container fluid>
                <Row>
                    <Col>
                        <h3>Class - Information</h3>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <BatchClassDetails batchData={batchDetails ?? []} />
                        <TrainingDates
                            batch={batchDetails}
                            type="attendance"
                            batchStudents={fetchBatchDetails}
                            defaultAccordion={defaultAccordion}
                        />
                    </Col>
                    <Col md={8} fluid>
                        <Row className="mb-2">
                            <Col>
                                <div className="p-2 fs-4 bg-light rounded">
                                    {selectedDate && selectedSession ? (
                                        <>
                                            <strong>Date:</strong>{" "}
                                            {selectedDate} &nbsp; | &nbsp;
                                            <strong>Session:</strong>{" "}
                                            {selectedSession.toUpperCase()}
                                        </>
                                    ) : (
                                        "Showing all training dates"
                                    )}
                                </div>
                            </Col>
                        </Row>
                        {selectedRows.length > 0 && (
                            <Row className="my-2">
                                <Col md={6}>
                                    <Form.Group>
                                        <Select
                                            className="is-invalid react-select-container z-2"
                                            classNamePrefix={"react-select"}
                                            options={addDefaultOption([
                                                {
                                                    value: "PRESENT",
                                                    label: "PRESENT",
                                                },
                                                {
                                                    value: "ABSENT",
                                                    label: "ABSENT",
                                                },
                                            ])}
                                            formatOptionLabel={(option) => {
                                                if (!option.value) {
                                                    return option.label; // "Select..."
                                                }

                                                return (
                                                    <Badge
                                                        className={`border w-full rounded-3 px-3 py-2 ${
                                                            STATUS_BADGE[
                                                                option.value
                                                            ] ||
                                                            STATUS_BADGE.DEFAULT
                                                        }`}
                                                    >
                                                        <span className="fw-bold">
                                                            {option.label}
                                                        </span>
                                                    </Badge>
                                                );
                                            }}
                                            onChange={(options) => {
                                                setAttendance(options?.value);
                                            }}
                                            name="bca-attendance"
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                                <Col col={6}>
                                    <Button
                                        variant="info"
                                        size="lg"
                                        onClick={createAttendance}
                                        disabled={loading}
                                    >
                                        Mark Attendance
                                    </Button>
                                </Col>
                            </Row>
                        )}
                        {error && (
                            <Alert variant="danger" className="px-3 py-2">
                                {error}
                            </Alert>
                        )}
                        <DynamicTable
                            className="table-layout-fixed"
                            dataPagination={{
                                data: batchDetails.batch_students,
                                meta: "",
                                links: "",
                            }}
                            withCheckbox
                            isFullPage={true}
                            isPaginated={false}
                            columns={columns}
                            onChangeSelectedRowsId={(selectedIds) => {
                                setForceSelectionIdxs(selectedIds);
                            }}
                            resetSelectedIdxs={resetSelectedIdxs}
                            canSelectRow={(row) => {
                                const bca =
                                    row.student_training_dates?.[0]
                                        ?.student_bca;

                                return bca !== undefined;
                            }}
                        />
                    </Col>
                </Row>
            </Container>
            <SnackbarAlert notif={notif} setNotif={setNotif} />
        </>
    );
};

export default ClassAbsentees;
